import { Calendar, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { formatDateRangeLabel } from '@/utils/dateRangePicker';
import type { DateRange } from '@/api/reportDateRanges';
import type { RecommendationRangePreset } from '@/hooks/useRecommendationDateRange';
import { WORKSPACE_DATE_PRESETS } from '@/constants/Constant_Preset_Variables';

type RecommendationDateRangeControlsProps = {
  preset: RecommendationRangePreset;
  onPresetChange: (preset: RecommendationRangePreset) => void;
  customRange: DateRange;
  onCustomRangeChange: (range: DateRange) => void;
  range: DateRange;
  invalidRange: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  layout?: 'default' | 'card';
};

const PRESETS: ReadonlyArray<{ id: RecommendationRangePreset; label: string }> = [
  ...WORKSPACE_DATE_PRESETS,
  { id: 'custom', label: 'Custom' },
];

export function RecommendationDateRangeControls({
  preset,
  onPresetChange,
  customRange,
  onCustomRangeChange,
  range,
  invalidRange,
  isFetching,
  onRefresh,
  layout = 'default',
}: RecommendationDateRangeControlsProps) {
  if (layout === 'card') {
    return (
      <div className="rounded-xl border border-border bg-surface p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div className="shrink-0">
            <span className="mb-3 block text-xs font-semibold uppercase tracking-wide text-foreground-muted">
              Period
            </span>
            <div className="flex flex-nowrap gap-2">
              {PRESETS.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => onPresetChange(id)}
                  className={`rounded-lg border px-3 py-1.5 text-left text-sm transition-colors ${
                    preset === id
                      ? 'border-brand bg-brand text-white'
                      : 'border-border bg-surface text-foreground-muted hover:bg-page'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {preset === 'custom' ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={customRange.startDate}
                  onChange={(e) =>
                    onCustomRangeChange({ ...customRange, startDate: e.target.value })
                  }
                  className="rounded-lg border border-border px-2 py-1.5 text-sm"
                />
                <span className="text-foreground-subtle">to</span>
                <input
                  type="date"
                  value={customRange.endDate}
                  onChange={(e) => onCustomRangeChange({ ...customRange, endDate: e.target.value })}
                  className="rounded-lg border border-border px-2 py-1.5 text-sm"
                />
              </div>
            ) : null}
          </div>

          {!invalidRange ? (
            <div className="shrink-0 pb-0.5">
              <p className="text-xs text-foreground-muted">Showing data for</p>
              <p className="mt-0.5 flex items-center gap-2 text-sm font-medium text-foreground">
                {formatDateRangeLabel(range.startDate, range.endDate)}
                <Calendar className="h-4 w-4 text-foreground-subtle" aria-hidden />
              </p>
            </div>
          ) : null}

          <div className="ml-auto shrink-0">
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={onRefresh}
              disabled={isFetching || invalidRange}
              className="whitespace-nowrap"
            >
              {isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden />
              )}
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div>
        <span className="mb-1 block text-xs font-medium text-foreground-muted">Period</span>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => onPresetChange(id)}
              className={`rounded-pill border px-3 py-1.5 text-sm ${
                preset === id
                  ? 'border-brand bg-brand text-white'
                  : 'border-border text-foreground-muted hover:bg-page'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {preset === 'custom' ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={customRange.startDate}
            onChange={(e) =>
              onCustomRangeChange({ ...customRange, startDate: e.target.value })
            }
            className="rounded-lg border border-border px-2 py-1.5 text-sm"
          />
          <span className="text-foreground-subtle">to</span>
          <input
            type="date"
            value={customRange.endDate}
            onChange={(e) => onCustomRangeChange({ ...customRange, endDate: e.target.value })}
            className="rounded-lg border border-border px-2 py-1.5 text-sm"
          />
        </div>
      ) : null}
      {preset !== 'custom' && !invalidRange ? (
        <p className="text-sm text-foreground-subtle">
          {range.startDate} → {range.endDate}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onRefresh}
        disabled={isFetching || invalidRange}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-page disabled:opacity-50"
      >
        {isFetching ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        )}
        Refresh
      </button>
    </div>
  );
}
